import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Icon, Table } from '@syneto/compass-react';
import AdminMaintenanceTypesEditModal from './adminMaintenanceTypesEditModal';
import Preloader from '../../../../components/preloader';
import DeleteMaintenanceType from './adminMaintenanceTypesDeleteModal';
import RemoteSupportToggle from './remoteSupportToggle';
import useMaintenancePlanRemoteSupport from '../../../../hooks/useMaintenancePlanRemoteSupport';
import { pushDangerNotification } from '../../../../data/notifications/actions';

function AdminMaintenanceTypesTable(props) {
	let { loadingMaintenancePlansReports, maintenancePlansReport, fetchMaintenancePlansReports, pushDangerNotification } = props;

	const [maintenancePlanDetails, setMaintenancePlanDetails] = useState(null);
	const [editModalOpened, setEditModalOpened] = useState(false);
	const [showDeleteModal, toggleDeleteModal] = useState(false);

	/*
	 * The rows come from central-api; the "Remote support" flag lives on rest-api and is read again
	 * whenever the row list changes, so a type added a moment ago shows its default (on).
	 */
	const remoteSupport = useMaintenancePlanRemoteSupport(maintenancePlansReport);

	useEffect(() => {
		if (remoteSupport.error) {
			pushDangerNotification(`Could not load the remote support settings. Reason: ${remoteSupport.error}`);
		}
	}, [remoteSupport.error]);

	/*
	 * The flag is merged into the rows rather than read inside the cell renderer, so the table sees
	 * a new data set when a switch flips and re-renders that row.
	 */
	const rows = (maintenancePlansReport ?? []).map((row) => {
		return { ...row, remoteSupportAllowed: remoteSupport.flags[String(row.id)] };
	});

	const saveRemoteSupport = async (maintenancePlanId, remoteSupportAllowed) => {
		const failure = await remoteSupport.save(maintenancePlanId, remoteSupportAllowed);

		if (failure) {
			pushDangerNotification(`Could not save the remote support setting. Reason: ${failure}`);
		}
	};

	const toggleRemoteSupport = (maintenancePlanId) => {
		return saveRemoteSupport(maintenancePlanId, !remoteSupport.flags[String(maintenancePlanId)]);
	};

	function openEditModal(maintenancePlan) {
		setEditModalOpened(true);
		setMaintenancePlanDetails(maintenancePlan);
	}

	function closeModal() {
		setEditModalOpened(false);
	}

	function openDeleteModal(maintenancePlan) {
		toggleDeleteModal(true);
		setMaintenancePlanDetails(maintenancePlan);
	}

	function renderCurrentlySelling(cell, row) {
		return (
			row.currentlySelling ? <span className="rounded-circle success"><Icon name="fa fa-check" /></span>
				: <span className="rounded-circle warning"><Icon name="fa fa-times" /></span>
		);
	}

	function renderRemoteSupport(cell, row) {
		return (
			<RemoteSupportToggle
				maintenancePlan={row}
				allowed={row.remoteSupportAllowed}
				saving={Boolean(remoteSupport.saving[String(row.id)])}
				disabled={remoteSupport.loading}
				onToggle={toggleRemoteSupport}
			/>
		);
	}

	function renderActions(cell, row) {
		return (
			<>
				<Button role="tertiary" onClick={() => { return openEditModal(row); }}>
					Edit
				</Button>
				<Button role="tertiary" disabled={row.count > 0} onClick={(e) => {
					openDeleteModal(row);
					e.stopPropagation();
				}}>
					Delete
				</Button>
			</>
		);
	}

	return (
		<div>
			{loadingMaintenancePlansReports ? <Preloader dataIsLoading={loadingMaintenancePlansReports} /> :
				<Table data={rows} rowEvents={{ onClick: (e, row) => { return openEditModal(row); } }}
					keyField="id">
					<Table.Col field="customerFacingName" align="left">
						External name
					</Table.Col>
					<Table.Col field="synetoInternalName" align="left">
						Internal name
					</Table.Col>
					<Table.Col field="currentlySelling" align="center" renderCell={renderCurrentlySelling}>
						Currently selling
					</Table.Col>
					<Table.Col field="remoteSupportAllowed" align="center" renderCell={renderRemoteSupport}>
						Remote support
					</Table.Col>
					<Table.Col field="count" align="left">
						Machines No
					</Table.Col>
					<Table.Col field="serenity" align="left">
						Serenity
					</Table.Col>
					<Table.Col align="center" renderCell={renderActions}>
						Actions
					</Table.Col>
				</Table>
			}

			<AdminMaintenanceTypesEditModal
				isOpen={editModalOpened}
				closeEditModal={closeModal}
				fetchMaintenancePlansReports={fetchMaintenancePlansReports}
				fetchMaintenancePlansOptions={props.fetchMaintenancePlansOptions}
				maintenancePlan={maintenancePlanDetails}
				remoteSupportAllowed={maintenancePlanDetails ? remoteSupport.flags[String(maintenancePlanDetails.id)] : undefined}
				saveRemoteSupport={saveRemoteSupport}
				toggleDeleteModal={toggleDeleteModal} />

			<DeleteMaintenanceType showDeleteModal={showDeleteModal}
				closeDeleteModal={() => { return toggleDeleteModal(false); }}
				maintenancePlan={maintenancePlanDetails}
				fetchMaintenancePlansReports={fetchMaintenancePlansReports}
				closeEditModal={closeModal} />
		</div>
	);
}

const mapDispatchToProps = (dispatch) => {
	return {
		pushDangerNotification: (message) => { return dispatch(pushDangerNotification(message)); }
	};
};

export default connect(null, mapDispatchToProps)(AdminMaintenanceTypesTable);
